import { Empty, Spin } from 'antd'
import {
  DocumentPreview,
  RolodexDocumentEntry,
  useLinkedDocuments,
} from 'features/files'
import React, { FC, useMemo } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import styled from 'styled-components'

import { useLibraDocument } from './LibraDocumentContext'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.forecast.background1};
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const EmptyStateDescription = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const FULL_HEIGHT_STYLE: React.CSSProperties = { height: '100%' }

export const EmbeddedDocumentPanel: FC = () => {
  const { selectedDocumentId, unitId } = useLibraDocument()
  const { ref: layoutRef, height } = useResizeDetector()

  const { documents, isLoading } = useLinkedDocuments(
    useMemo(() => (unitId ? { unit: unitId } : undefined), [unitId])
  )

  const selectedDocument = useMemo<RolodexDocumentEntry | null>(() => {
    if (!selectedDocumentId) return null
    return documents.find((doc) => doc.entry_id === selectedDocumentId) ?? null
  }, [documents, selectedDocumentId])

  if (isLoading) {
    return (
      <PanelContainer>
        <EmptyStateContainer>
          <Spin size='large' />
        </EmptyStateContainer>
      </PanelContainer>
    )
  }

  if (documents.length === 0) {
    return (
      <PanelContainer>
        <EmptyStateContainer>
          <Empty
            description={
              <>
                <EmptyStateTitle>No documents available</EmptyStateTitle>
                <EmptyStateDescription>
                  Upload documents in the Data Health step
                </EmptyStateDescription>
              </>
            }
          />
        </EmptyStateContainer>
      </PanelContainer>
    )
  }

  return (
    <PanelContainer ref={layoutRef}>
      {selectedDocument ? (
        <DocumentPreview
          document={selectedDocument}
          height={height ? height - 20 : undefined}
          containerStyle={FULL_HEIGHT_STYLE}
          continuousScroll={true}
        />
      ) : (
        <EmptyStateContainer>
          <Empty
            description={
              <>
                <EmptyStateTitle>No document selected</EmptyStateTitle>
                <EmptyStateDescription>
                  Select a document from the navigator to preview
                </EmptyStateDescription>
              </>
            }
          />
        </EmptyStateContainer>
      )}
    </PanelContainer>
  )
}
